/**
 * Handler Registry
 *
 * Manages registration of event handlers with collision detection.
 */

import type {
	RegisteredHandler,
	HandlerOptions,
	Handler,
	ShortcutInfo,
	CommandInfo,
	CommandSpec,
	ParsedSequence,
	LogLevel,
	Platform,
} from './types';
import { parseShortcut } from './matcher';
import { shortcutToDisplay } from './platform';

export interface Registry {
	/** Register a handler */
	register(
		eventType: string,
		shortcut: string | null,
		options: HandlerOptions,
		handler: Handler<any>
	): () => void;

	/** Get all handlers for an event type, sorted by priority */
	getHandlers(eventType: string): RegisteredHandler[];

	/** Get all registered shortcuts with metadata */
	getShortcuts(): ShortcutInfo[];

	/** Get all registered commands */
	getCommands(): CommandInfo[];

	/** Get a specific command by ID */
	getCommand(commandId: string): CommandInfo | undefined;

	/** Execute a command by ID */
	executeCommand(commandId: string): boolean;

	/** Set or clear the shortcut for a command */
	setShortcut(commandId: string, shortcut: string | null): void;

	/** Set log level */
	setLogLevel(level: LogLevel): void;

	/** Mark a handler as called (for testing) */
	recordCall(handlerId: number, args: any[]): void;

	/** Get calls for a handler (for testing) */
	getCalls(shortcut: string): any[];
}

/**
 * Generate a command ID from a name.
 * Converts "Copy Selection" to "command.copy-selection"
 */
function generateCommandId(name: string): string {
	return 'command.' + name.toLowerCase().replace(/\s+/g, '-');
}

/**
 * Normalize command option to CommandSpec.
 * Accepts string (name only) or full CommandSpec.
 */
function normalizeCommand(command: string | CommandSpec): CommandSpec {
	if (typeof command === 'string') {
		return { id: generateCommandId(command), name: command };
	}
	return command;
}

export function createRegistry(platform: Platform, logLevel: LogLevel = 'warn'): Registry {
	let level = logLevel;
	let nextId = 0;
	const handlers: RegisteredHandler[] = [];
	const handlerCalls: Map<number, any[][]> = new Map();
	// Command registry: maps command ID to handler
	const commands: Map<string, RegisteredHandler> = new Map();

	function log(type: 'warn' | 'info' | 'debug', message: string): void {
		if (level === 'silent') return;
		if (type === 'warn' && (level === 'warn' || level === 'info' || level === 'debug')) {
			console.warn(`[input] ${message}`);
		} else if (type === 'info' && (level === 'info' || level === 'debug')) {
			console.info(`[input] ${message}`);
		} else if (type === 'debug' && level === 'debug') {
			console.debug(`[input] ${message}`);
		}
	}

	function checkCollision(
		eventType: string,
		shortcut: string | null,
		mode: string,
		priority: number
	): void {
		if (!shortcut) return;

		const existing = handlers.find(
			(h) =>
				h.eventType === eventType &&
				h.shortcut === shortcut &&
				h.mode === mode &&
				h.priority === priority
		);

		if (existing) {
			log(
				'warn',
				`Collision: ${eventType} '${shortcut}' already registered for mode '${mode}' at priority ${priority}`
			);
		}
	}

	function checkCommandDuplicate(commandId: string): void {
		if (commands.has(commandId)) {
			throw new Error(`Duplicate command ID: '${commandId}' is already registered`);
		}
	}

	/**
	 * Check if an event type is a keyboard event.
	 */
	function isKeyboardEvent(eventType: string): boolean {
		return eventType === 'keydown' || eventType === 'keyup' || eventType === 'keypress';
	}

	return {
		register(
			eventType: string,
			shortcut: string | null,
			options: HandlerOptions,
			handler: Handler<any>
		): () => void {
			const mode = options.mode ?? 'default';
			const priority = options.priority ?? 0;

			// Check for collisions
			checkCollision(eventType, shortcut, mode, priority);

			// Parse shortcut if present
			let parsedSequence: ParsedSequence | null = null;
			if (shortcut) {
				parsedSequence = parseShortcut(shortcut, options.sequenceTimeout);
			}

			// Handle command registration
			let commandId: string | undefined;
			let commandName: string | undefined;

			if (options.command) {
				const cmd = normalizeCommand(options.command);
				checkCommandDuplicate(cmd.id);
				commandId = cmd.id;
				commandName = cmd.name;
			} else if (isKeyboardEvent(eventType) && shortcut) {
				// Warn about missing command for keyboard shortcuts with shortcuts
				log(
					'warn',
					`Keyboard shortcut '${shortcut}' registered without command metadata. ` +
						`Add { command: 'Name' } or { command: { id: 'id', name: 'Name' } } for discoverability.`
				);
			}

			const id = nextId++;
			const entry: RegisteredHandler = {
				id,
				eventType,
				shortcut,
				parsedSequence,
				options,
				handler,
				mode,
				priority,
				commandId,
				commandName,
			};

			handlers.push(entry);
			handlerCalls.set(id, []);

			// Register in command registry if it's a command
			if (commandId) {
				commands.set(commandId, entry);
			}

			// Return unregister function
			return () => {
				const index = handlers.indexOf(entry);
				if (index !== -1) {
					handlers.splice(index, 1);
				}
				handlerCalls.delete(id);
				// Remove from command registry
				if (commandId) {
					commands.delete(commandId);
				}
			};
		},

		getHandlers(eventType: string): RegisteredHandler[] {
			return handlers
				.filter((h) => h.eventType === eventType)
				.sort((a, b) => {
					// Higher priority first
					if (b.priority !== a.priority) {
						return b.priority - a.priority;
					}
					// Then by registration order (lower id = registered first)
					return a.id - b.id;
				});
		},

		getShortcuts(): ShortcutInfo[] {
			return handlers
				.filter((h) => h.shortcut && h.options.description)
				.map((h) => ({
					shortcut: h.shortcut!,
					display: shortcutToDisplay(h.shortcut!, platform),
					description: h.options.description,
					category: h.options.category,
					mode: h.mode,
				}));
		},

		getCommands(): CommandInfo[] {
			return Array.from(commands.values()).map((h) => ({
				id: h.commandId!,
				name: h.commandName!,
				category: h.options.category,
				shortcut: h.shortcut,
				display: h.shortcut ? shortcutToDisplay(h.shortcut, platform) : null,
				mode: h.mode,
			}));
		},

		getCommand(commandId: string): CommandInfo | undefined {
			const h = commands.get(commandId);
			if (!h) return undefined;
			return {
				id: h.commandId!,
				name: h.commandName!,
				category: h.options.category,
				shortcut: h.shortcut,
				display: h.shortcut ? shortcutToDisplay(h.shortcut, platform) : null,
				mode: h.mode,
			};
		},

		executeCommand(commandId: string): boolean {
			const handler = commands.get(commandId);
			if (!handler) {
				log('warn', `Command '${commandId}' not found`);
				return false;
			}

			// Create a minimal context for command execution
			const ctx = {
				event: new Event('command'),
				target: document?.body ?? ({} as Element),
				modifiers: { ctrl: false, alt: false, shift: false, meta: false, mod: false },
				stopPropagation: () => {},
			};

			try {
				handler.handler(ctx);
				return true;
			} catch (err) {
				log('warn', `Error executing command '${commandId}': ${err}`);
				return false;
			}
		},

		setShortcut(commandId: string, newShortcut: string | null): void {
			const handler = commands.get(commandId);
			if (!handler) {
				log('warn', `Command '${commandId}' not found, cannot remap shortcut`);
				return;
			}

			// Update the shortcut
			handler.shortcut = newShortcut;

			// Re-parse the sequence if shortcut changed
			if (newShortcut) {
				handler.parsedSequence = parseShortcut(newShortcut, handler.options.sequenceTimeout);
			} else {
				handler.parsedSequence = null;
			}

			log('info', `Remapped command '${commandId}' to shortcut '${newShortcut ?? 'none'}'`);
		},

		setLogLevel(newLevel: LogLevel): void {
			level = newLevel;
		},

		recordCall(handlerId: number, args: any[]): void {
			const calls = handlerCalls.get(handlerId);
			if (calls) {
				calls.push(args);
			}
		},

		getCalls(shortcut: string): any[] {
			const handler = handlers.find((h) => h.shortcut === shortcut);
			if (!handler) return [];
			return handlerCalls.get(handler.id) || [];
		},
	};
}
